/* eslint-disable prefer-destructuring */

import Riot from 'riot';
import $ from 'jquery';
import { ActionTypes } from '../constants/ActionTypes';
import { NotificationUtils } from '../utils/NotificationUtils';
import { NotificationActions } from '../constants/NotificationActions';
import { NotificationConstants } from '../constants/NotificationConstants';
import { Dispatcher } from '../dispatcher/Dispatcher';

const CHANGE_EVENT = 'update';

// State
const _notifications = {};
const _checkedTimeCache = {};
let _settings = {};
let _settingsLoading = false;

const _filterHorizons = {
  // e.g. NotificationActions.ACTION_ALL: {
  //     true: {
  //         eariestPage: 3,
  //         earliestTimestamp: "34241343.05243Z",
  //         complete: false,
  //         loading: false
  //     }
  // }
};

// Event emitter
const _observable = {};
Riot.observable(_observable);

// Helper functions to manipulate data

const _initFilter = (actionType, includeRead) => {
  let firstUpdate = false;
  if (!_filterHorizons[actionType]) {
    _filterHorizons[actionType] = {};
  }
  if (!_filterHorizons[actionType][includeRead]) {
    firstUpdate = true;
    _filterHorizons[actionType][includeRead] = {
      earliestPage: 0,
      earliestTimestamp: null,
      complete: false,
      loading: false,
    };
  }
  return firstUpdate;
};

const _allowAllFilter = () => {
  return true;
};

const _notificationFilter = (
  actionType,
  includeRead,
  includeExtras = false
) => {
  return (notification) => {
    if (!includeExtras) {
      // Don't return anything unless we know we have all of them
      if (!_filterHorizons[actionType][includeRead].earliestTimestamp) {
        return false;
      } else if (
        notification.timestamp <
        _filterHorizons[actionType][includeRead].earliestTimestamp
      ) {
        return false;
      }
    }
    if (
      actionType === NotificationActions.ACTION_ALL ||
      notification.action === actionType
    ) {
      return includeRead || notification.isUnread();
    } else {
      return false;
    }
  };
};

const _getNotifications = (filter = _allowAllFilter, sort = true) => {
  const notifications = [];
  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const notificationId in _notifications) {
    const notification = _notifications[notificationId].copy();
    if (filter(notification)) {
      notifications.push(notification);
    }
  }
  if (sort) {
    return notifications.sort((a, b) => {
      if (a.timestamp < b.timestamp) {
        return 1;
      } else if (a.timestamp > b.timestamp) {
        return -1;
      } else {
        return 0;
      }
    });
  } else {
    return notifications;
  }
};

const _updateFilterHorizons = (
  actionType,
  includeRead,
  earliestTimestamp,
  complete
) => {
  _initFilter(actionType, includeRead);

  // Update the filter horizons for all sub filters aswell
  if (actionType === NotificationActions.ACTION_ALL) {
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const subActionName in NotificationActions) {
      const subAction = NotificationActions[subActionName];
      if (subAction !== NotificationActions.ACTION_ALL) {
        _updateFilterHorizons(
          subAction,
          includeRead,
          earliestTimestamp,
          complete
        );
      }
      if (includeRead) {
        _updateFilterHorizons(subAction, false, earliestTimestamp, complete);
      }
    }
  }

  // Earliest page for this filter whose contents is known
  const numNotifications = _getNotifications(
    _notificationFilter(actionType, includeRead, true)
  ).length;
  const earliestPage = Math.floor(
    numNotifications / NotificationConstants.ITEMS_PER_PAGE
  );
  if (earliestPage > _filterHorizons[actionType][includeRead].earliestPage) {
    _filterHorizons[actionType][includeRead].earliestPage = earliestPage;
  }

  // Earliest timestamp for which all subsequent notifications for this filter are available
  const currentTimestamp =
    _filterHorizons[actionType][includeRead].earliestTimestamp;
  if (
    earliestTimestamp &&
    (!currentTimestamp || earliestTimestamp < currentTimestamp)
  ) {
    _filterHorizons[actionType][
      includeRead
    ].earliestTimestamp = earliestTimestamp;
  }

  // Whether all the past notifications for this filter are available
  if (complete) {
    _filterHorizons[actionType][includeRead].complete = complete;
  }

  _filterHorizons[actionType][includeRead].loading = false;
};

const _setAllNotificationsChecked = (lastCheckedTime) => {
  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const notificationId in _notifications) {
    const notification = _notifications[notificationId];
    if (lastCheckedTime) {
      notification.lastCheckedTime = lastCheckedTime;
    } else {
      notification.lastCheckedTime = notification.timestamp;
    }
  }
};

const _setNotificationChecked = (notificationId, lastCheckedTime) => {
  const notification = _notifications[notificationId];
  if (lastCheckedTime) {
    notification.lastCheckedTime = lastCheckedTime;
  } else {
    notification.lastCheckedTime = notification.timestamp;
  }
};

const _setLoading = (actionType, includeRead, loading) => {
  _initFilter(actionType, includeRead);
  _filterHorizons[actionType][includeRead].loading = loading;
};

// Interface to view-controllers
export const NotificationStore = {
  dispatchToken: null,

  registerChangeCallback: (callback) => {
    _observable.on(CHANGE_EVENT, callback);
  },

  unregisterChangeCallback: (callback) => {
    _observable.off(CHANGE_EVENT, callback);
  },

  getNotification: (notificationId) => {
    const notification = _notifications[notificationId];
    if (notification) {
      return notification.copy();
    } else {
      return null;
    }
  },

  getNotificationForSubscription: (subscriptionId) => {
    const notifications = _getNotifications((notification) => {
      return notification.subscriptionId === subscriptionId;
    });
    if (notifications.length === 0) {
      return null;
    } else {
      return notifications[0];
    }
  },

  getNotifications: (
    actionType = NotificationActions.ACTION_ALL,
    includeRead = false
  ) => {
    _initFilter(actionType, includeRead);
    return _getNotifications(_notificationFilter(actionType, includeRead));
  },

  allNotificationsDownloaded: (
    actionType = NotificationActions.ACTION_ALL,
    includeRead = false
  ) => {
    _initFilter(actionType, includeRead);
    return _filterHorizons[actionType][includeRead].complete;
  },

  loadingMoreNotifications: (
    actionType = NotificationActions.ACTION_ALL,
    includeRead = false
  ) => {
    _initFilter(actionType, includeRead);
    return _filterHorizons[actionType][includeRead].loading;
  },

  hasStartedLoadingNotifications: (
    actionType = NotificationActions.ACTION_ALL,
    includeRead = false
  ) => {
    _initFilter(actionType, includeRead);
    return (
      _filterHorizons[actionType][includeRead].loading ||
      _filterHorizons[actionType][includeRead].loading ||
      _filterHorizons[actionType][includeRead].earliestTimestamp
    );
  },

  nextPageIndex: (
    actionType = NotificationActions.ACTION_ALL,
    includeRead = false
  ) => {
    _initFilter(actionType, includeRead);
    return _filterHorizons[actionType][includeRead].earliestPage + 1;
  },

  getUnreadCount: () => {
    return _getNotifications(
      _notificationFilter(NotificationActions.ACTION_ALL, false, true)
    ).length;
  },

  getTotalCount: () => {
    return NotificationStore.getNotifications(
      NotificationActions.ACTION_ALL,
      true
    ).length;
  },

  isUnreadCountUnderestimate: () => {
    /* eslint-disable dot-notation */
    _initFilter(NotificationActions.ACTION_ALL, false);
    if (
      _filterHorizons[NotificationActions.ACTION_ALL][false]
        .earliestTimestamp == null
    ) {
      return true;
    } else {
      return !_filterHorizons[NotificationActions.ACTION_ALL][false].complete;
    }
  },

  isTotalCountUnderestimate: () => {
    /* eslint-disable dot-notation */
    if (
      _filterHorizons[NotificationActions.ACTION_ALL][true].earliestTimestamp ==
      null
    ) {
      return true;
    } else {
      return !_filterHorizons[NotificationActions.ACTION_ALL][true].complete;
    }
  },

  getSettings: () => {
    const result = {};
    $.extend(result, _settings, true);
    return result;
  },

  settingsLoading: () => {
    return _settingsLoading;
  },
};

// Responses to actions from dispatcher
NotificationStore.dispatchToken = Dispatcher.register((action) => {
  let notification;
  let actionType;
  let includeRead;

  // eslint-disable-next-line default-case
  switch (action.type) {
    case ActionTypes.NOTIFICATIONS_SEEN: {
      const notifications = NotificationStore.getNotifications();
      if (notifications.length) {
        _observable.trigger(CHANGE_EVENT);
      }
      break;
    }

    case ActionTypes.NOTIFICATIONS_SEEN_TIME:
      _observable.trigger(CHANGE_EVENT);
      break;

    case ActionTypes.NOTIFICATION_CHECKED_TIME:
      notification = NotificationStore.getNotificationForSubscription(
        action.subscriptionId
      );
      if (notification) {
        _setNotificationChecked(notification.id, action.lastCheckedTime);
        _observable.trigger(CHANGE_EVENT);
      } else {
        _checkedTimeCache[action.subscriptionId] = action.lastCheckedTime;
      }
      break;

    case ActionTypes.NOTIFICATIONS_ALL_CHECKED:
      _setAllNotificationsChecked();
      _observable.trigger(CHANGE_EVENT);
      break;

    case ActionTypes.NOTIFICATIONS_ALL_CHECKED_TIME:
      _setAllNotificationsChecked(action.lastCheckedTime);
      _observable.trigger(CHANGE_EVENT);
      break;

    case ActionTypes.NOTIFICATION_UPDATE: {
      action.notifications.forEach((currentNotification) => {
        if (_checkedTimeCache[currentNotification.subscriptionId]) {
          if (
            currentNotification.lastCheckedTime <
            _checkedTimeCache[currentNotification.subscriptionId]
          ) {
            // eslint-disable-next-line no-param-reassign
            currentNotification.lastCheckedTime =
              _checkedTimeCache[currentNotification.subscriptionId];
          }
          delete _checkedTimeCache[currentNotification.subscriptionId];
        }
        const existingNotification = _notifications[currentNotification._id];
        const processedNotification = NotificationUtils.convertRawNotification(
          currentNotification,
          existingNotification
        );
        _notifications[processedNotification.id] = processedNotification;
      });
      _observable.trigger(CHANGE_EVENT);
      break;
    }

    case ActionTypes.NOTIFICATION_FILTER_STATUS: {
      actionType = action.actionType;
      includeRead = action.includeRead;
      const earliestTime = action.earliestTime;
      const complete = action.complete;
      _updateFilterHorizons(actionType, includeRead, earliestTime, complete);
      _observable.trigger(CHANGE_EVENT);
      break;
    }

    case ActionTypes.NOTIFICATION_LOADING_STATUS:
      actionType = action.actionType;
      includeRead = action.includeRead;
      _setLoading(actionType, includeRead, action.loading);
      _observable.trigger(CHANGE_EVENT);
      break;

    case ActionTypes.NOTIFICATION_SETTINGS_LOADING:
      _settingsLoading = true;
      _observable.trigger(CHANGE_EVENT);
      break;

    case ActionTypes.NOTIFICATION_SETTINGS_UPDATE:
      _settingsLoading = false;
      _settings = NotificationUtils.clientSettingsFromServer(action.settings);
      _observable.trigger(CHANGE_EVENT);
      break;
  }
});
